/**
 * Lightweight pre-match error banner for `state.serverError` (#332).
 *
 * Before this existed, a rejected `createMatch`/`joinMatch`/`quickJoin`
 * (server `{ type: "error", code }`) was received and stored
 * (`WsClientProvider`'s `dispatchServerMessage`) but never rendered
 * anywhere — `HomeScreen`'s effect only used it to reset its own local
 * `pending` state. A player saw a button silently do nothing, repeatedly,
 * with zero feedback (see #332's root-cause writeup). This surfaces the
 * SAME `serverError` the reducer already tracks, app-wide (mounted in
 * `App.tsx` next to `DisconnectBanner`/`ProtocolMismatchPanel`), so it's
 * visible regardless of which screen triggered the rejection.
 *
 * Auto-dismisses after `SERVER_ERROR_TTL_MS` (a transient toast, not a
 * blocking modal — the rejection has already been handled by the time the
 * player reads it) and is manually dismissable. Suppressed while the
 * disconnect banner or protocol-mismatch panel already own the screen —
 * those are the more specific, more actionable overlays.
 */

import { useEffect, type ReactElement } from "react";
import { useStore } from "../store/context.js";

/** How long the banner stays up before auto-dismissing. */
export const SERVER_ERROR_TTL_MS = 6000;

/** Short, human-facing text per `ErrorCode` (see `@pit/protocol`). */
function messageFor(code: string): string {
  switch (code) {
    case "unauthorized":
      return "That action needs a connection handshake first — please retry.";
    case "badMessage":
      return "That action couldn't be completed right now. Please try again.";
    case "rateLimited":
      return "You're doing that too fast — please slow down.";
    case "oversized":
      return "A message to the server was rejected (too large).";
    case "internal":
      return "A server error occurred. Please try again.";
    case "botAttachFailed":
      return "The bot opponent couldn't be loaded. Please try creating the match again.";
    default:
      return `Something went wrong (${code}). Please try again.`;
  }
}

export function ServerErrorBanner(): ReactElement | null {
  const { state, dispatch } = useStore();
  const err = state.serverError;

  useEffect(() => {
    if (err === null) return;
    const id = window.setTimeout(() => {
      dispatch({ type: "serverError.clear" });
    }, SERVER_ERROR_TTL_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [err, dispatch]);

  if (err === null) return null;
  // Yield to the more specific overlays — a dead connection or a stale
  // protocol are more actionable/urgent than a transient rejection.
  if (state.connection.kind === "closed") return null;
  if (state.connection.kind === "protocolMismatch") return null;

  return (
    <div
      data-testid="server-error-banner"
      data-code={err.code}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        padding: "8px 16px",
        background: "#5a3d1a",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        zIndex: 40,
      }}
    >
      <span>{messageFor(err.code)}</span>
      <button
        type="button"
        data-testid="server-error-dismiss"
        onClick={() => {
          dispatch({ type: "serverError.clear" });
        }}
        style={{
          padding: "4px 10px",
          background: "#fff",
          color: "#5a3d1a",
          border: "none",
          borderRadius: 4,
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
